import { Box, Flex, Heading, Text, Table, Tabs } from "@radix-ui/themes";
import { useMemo } from "react";
import { CategoryHeatMap } from "@/components/category-heat-map";
import { generateCategoryColors } from "@/utils/color-generator";

interface Category {
  name: string;
  description: string;
  count: number;
}

interface CategoryWithColor extends Category {
  color: string;
}

interface AnalyzedColumn {
  id: number;
  name: string;
  tab_name: string;
  categories: Category[];
}

interface AnalysisResultsProps {
  analysisStatus: string;
  analyzedColumns: AnalyzedColumn[];
}

export function AnalysisResults({
  analysisStatus,
  analyzedColumns,
}: AnalysisResultsProps) {
  // Generate colors for all columns' categories
  const columnsWithColors = useMemo(() => {
    return analyzedColumns.map((column) => {
      const colors = generateCategoryColors(column.categories.length);
      const categoriesWithColors: CategoryWithColor[] = column.categories.map(
        (cat, idx) => ({
          ...cat,
          color: colors[idx],
        })
      );
      return {
        ...column,
        categories: categoriesWithColors,
      };
    });
  }, [analyzedColumns]);

  // Don't show anything if waiting
  if (analysisStatus === "waiting") {
    return null;
  }

  // Show processing state
  if (analysisStatus === "processing") {
    return (
      <Box mb="6">
        <Heading size="5" mb="4">
          Analysis Results
        </Heading>
        <Box
          style={{
            padding: "var(--space-6)",
            borderRadius: "var(--radius-3)",
            border: "1px solid var(--gray-6)",
            backgroundColor: "var(--gray-2)",
            textAlign: "center",
          }}
        >
          <Text size="3" style={{ color: "var(--gray-11)" }}>
            Analysis is being processed... This may take a few minutes.
          </Text>
        </Box>
      </Box>
    );
  }

  // Show results when finished
  if (analysisStatus === "finished" && analyzedColumns.length > 0) {
    return (
      <Box mb="6">
        <Heading size="5" mb="4">
          Analysis Results
        </Heading>

        <Tabs.Root defaultValue={columnsWithColors[0]?.id.toString()}>
          <Tabs.List>
            {columnsWithColors.map((column) => (
              <Tabs.Trigger key={column.id} value={column.id.toString()}>
                {column.name}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {columnsWithColors.map((column) => {
            // Calculate total count for percentage calculation
            const totalCount = column.categories.reduce(
              (sum, cat) => sum + cat.count,
              0
            );

            return (
              <Tabs.Content key={column.id} value={column.id.toString()}>
                <Box mt="4" mb="6">
                  <CategoryHeatMap categories={column.categories} />
                </Box>

                <Box mt="4">
                  <Table.Root variant="surface">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeaderCell>
                          Category
                        </Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>Count</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>
                          % of Total
                        </Table.ColumnHeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {column.categories.map((category, idx) => {
                        const percentage =
                          totalCount > 0
                            ? ((category.count / totalCount) * 100).toFixed(1)
                            : "0.0";

                        return (
                          <Table.Row key={idx}>
                            <Table.Cell>
                              <Flex direction="column" gap="1">
                                <Flex align="center" gap="2">
                                  <Box
                                    style={{
                                      width: "1em",
                                      height: "1em",
                                      borderRadius: "50%",
                                      backgroundColor: category.color,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Text weight="bold">{category.name}</Text>
                                </Flex>
                                <Text
                                  size="2"
                                  style={{ color: "var(--gray-11)" }}
                                >
                                  {category.description}
                                </Text>
                              </Flex>
                            </Table.Cell>
                            <Table.Cell>
                              <Text weight="bold">{category.count}</Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Text weight="bold">{percentage}%</Text>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table.Root>
                </Box>
              </Tabs.Content>
            );
          })}
        </Tabs.Root>
      </Box>
    );
  }

  // Show error state if failed
  if (analysisStatus === "failed") {
    return (
      <Box mb="6">
        <Heading size="5" mb="4">
          Analysis Results
        </Heading>
        <Box
          style={{
            padding: "var(--space-6)",
            borderRadius: "var(--radius-3)",
            border: "1px solid var(--red-6)",
            backgroundColor: "var(--red-2)",
            textAlign: "center",
          }}
        >
          <Text size="3" style={{ color: "var(--red-11)" }}>
            Analysis failed. Please try again.
          </Text>
        </Box>
      </Box>
    );
  }

  return null;
}
